/**
 * Modal wall-height statistics shared between the web-app roof builder
 * and server-side multi-story registration.
 *
 * `modalWallTopY` picks the dominant wall-top elevation across a set of
 * observed tops, robust to a handful of outliers (a stairwell void or a
 * single vaulted pop-up). Roof generation uses it to place the roof
 * base; multi-story registration uses it to find a story's ceiling plane
 * (where the floor above stacks). One implementation so the two never
 * drift — previously web-app-only in
 * `web-app/src/utils/roofGeometry/footprint.js`.
 */

const { wallOutlineEnds, END_TOLERANCE_M } = require('./wallOutlineEnds');

const WALL_TOP_BUCKET = 0.05; // 5 cm — modal-height bucket size
const MULTIMODAL_RATIO = 0.8; // runner-up bucket within 80% of winner → multimodal

/**
 * Pick the most common wall-top elevation across all observed top
 * corners. When the distribution is multimodal (e.g. an exterior-wall set
 * spanning both floors of a two-story house), fall back to the max
 * elevation so upper-story walls don't poke through the roof. For a
 * single-story set with one or two anomalous walls (stairwell void,
 * vaulted pop-up) this picks the dominant ceiling height instead of being
 * dragged up by the outliers.
 *
 * @param {number[]} tops — wall-top Y elevations (absolute world Y).
 * @returns {number|null} modal top-Y, or null for an empty input.
 */
const modalWallTopY = (tops) => {
  if (tops.length === 0) return null;
  const buckets = new Map();
  for (const y of tops) {
    const key = Math.round(y / WALL_TOP_BUCKET);
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  const sorted = [...buckets.entries()].sort((a, b) => b[1] - a[1]);
  if (sorted.length > 1 && sorted[1][1] >= sorted[0][1] * MULTIMODAL_RATIO) {
    return Math.max(...tops);
  }
  return sorted[0][0] * WALL_TOP_BUCKET;
};

/**
 * Top Y of a box-primitive wall (structure-wall shape: center.y +
 * dimensions.y/2). Mirrors `wallBaseY` from floor-bands.js.
 */
const wallTopY = (wall) =>
  (wall.center?.y ?? 0) + (wall.dimensions?.y ?? 0) / 2;

/**
 * Modal ceiling-plane Y for a set of box-primitive structure walls — the
 * elevation where the story above stacks. null if no walls.
 *
 * @param {Array} walls — structure walls ({ center, dimensions }).
 */
const modalCeilingY = (walls) => {
  if (!walls || walls.length === 0) return null;
  return modalWallTopY(walls.map(wallTopY));
};

const cornerPoint = (c) =>
  Array.isArray(c) ? { x: c[0], y: c[1], z: c[2] } : c;

const isFinitePoint = (p) =>
  !!p &&
  [p.x, p.y, p.z].every((v) => typeof v === 'number' && Number.isFinite(v));

/**
 * A wall outline laid out in the wall's own plane: `t` along the wall from
 * one end (`wallOutlineEnds`), `y` up. null when it has no length in plan.
 */
const outlineAlongWall = (points) => {
  const ends = wallOutlineEnds(points);
  if (!ends) return null;
  const [a, b] = ends;
  const len = Math.hypot(b.x - a.x, b.z - a.z);
  if (!(len > 0)) return null;
  const ux = (b.x - a.x) / len;
  const uz = (b.z - a.z) / len;
  return points.map((p) => ({
    t: (p.x - a.x) * ux + (p.z - a.z) * uz,
    y: p.y,
  }));
};

// An edge leaning further than this share of its rise is a SLOPE, not a side
// or riser. On housfully-dev (2026-11-03) the sides of level-topped walls
// lean up to 2% of their rise, with two outliers at 6% and 15% (stubs in one
// structure). A roof slope runs at least half its rise even at a 24/12 pitch.
const SIDE_LEAN_RATIO = 0.25;

/**
 * An outline's TOP edge: the heights of its corners, and the most any slope
 * along it rises.
 *
 * Which side of an edge the wall is on follows from the way the outline runs
 * along it. Walked counter-clockwise (t right, y up), an edge heading back
 * along the wall has the wall BELOW it: it is part of the top. One heading
 * forward has the wall ABOVE it: it is a floor. The top's corners are the
 * ends of top edges, less any corner a floor edge also ends at — the foot of
 * an end side, or of a riser where a split-level floor steps up.
 *
 * Only an edge that RUNS further than it rises can make its corners floor.
 * Sides and risers stand vertical but lean a few mm to a few cm either way
 * (the corpus: up to 2% of their rise, while its steepest roof slope runs
 * half its rise), so which way a riser on a stepped top leans is noise.
 * Read as a floor, it would take both its corners off the top and could
 * hide the whole step.
 *
 * Repeated corners (within `END_TOLERANCE_M` of the next one) are merged
 * first: the copy of a side's foot is not on the floor edge, and would put
 * the floor's height on the top.
 *
 * The slope's rise is what catches a top that comes down to meet the floor.
 * That shared corner is a floor corner, so it leaves the corner heights,
 * and a triangle (a gable reaching the floor, no level part) would keep only
 * its ridge and read as level.
 */
const readTopEdge = (laidOut) => {
  const samePlace = (p, q) =>
    Math.abs(p.t - q.t) <= END_TOLERANCE_M &&
    Math.abs(p.y - q.y) <= END_TOLERANCE_M;
  const outline = laidOut.filter(
    (p, i) => !samePlace(p, laidOut[(i + 1) % laidOut.length])
  );
  const n = outline.length;
  let doubleArea = 0;
  for (let i = 0; i < n; i++) {
    const p = outline[i];
    const q = outline[(i + 1) % n];
    doubleArea += p.t * q.y - q.t * p.y;
  }
  const winding = Math.sign(doubleArea);
  const onTop = new Array(n).fill(false);
  const onFloor = new Array(n).fill(false);
  const topEdges = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const run = outline[j].t - outline[i].t;
    const rise = Math.abs(outline[j].y - outline[i].y);
    if (run * winding < 0) {
      onTop[i] = onTop[j] = true;
      topEdges.push({ i, j, run, rise });
    } else if (run * winding > 0 && Math.abs(run) > rise) {
      onFloor[i] = onFloor[j] = true;
    }
  }
  const isTopCorner = (k) => onTop[k] && !onFloor[k];
  // A slope counts only when it reaches the top: one leaning back between
  // two floor corners is a riser in a stepped floor (a stair stringer's
  // underside), not part of the top.
  let slopeRise = 0;
  for (const { i, j, run, rise } of topEdges) {
    if (!isTopCorner(i) && !isTopCorner(j)) continue;
    if (Math.abs(run) > SIDE_LEAN_RATIO * rise) {
      slopeRise = Math.max(slopeRise, rise);
    }
  }
  const heights = outline.filter((_, k) => isTopCorner(k)).map((p) => p.y);
  return { heights, slopeRise };
};

/**
 * Is a wall's top edge one horizontal line? Crown molding (and anything
 * else that runs along a level top) is only valid on such walls — a
 * raked gable side or a stepped top cannot carry it.
 *
 * The top is read off the outline's own top edge (`readTopEdge`) and is
 * flat when every corner on it sits within `tol` of the others and no slope
 * along it rises more than `tol`. Tolerance
 * defaults to WALL_TOP_BUCKET (5 cm), the same bucket the modal-top stats
 * treat as "one elevation". A wall without a polygon outline (box
 * primitive) is flat by construction. Corners may be {x, y, z} objects or
 * [x, y, z] arrays, in world space with y up.
 *
 * It used to take the corners above the wall's MID-HEIGHT as the top. On a
 * two-storey stairwell wall that steps to one storey, the step sits just
 * under the middle (one storey is a little under half of two, which also
 * holds the floor between them), so the step was ignored and the crown ran
 * at the two-storey top along the whole wall, in mid-air over the
 * one-storey part, and the estimate priced it there (issue #3081: corners
 * at -4.08, -1.62 and 1.00, middle -1.54). A shed slope whose low end is
 * under the middle was missed the same way.
 */
const wallHasFlatTop = (wall, tol = WALL_TOP_BUCKET) => {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) return true;
  const points = corners.map(cornerPoint).filter(isFinitePoint);
  if (points.length < 3) return true;
  const ys = points.map((p) => p.y);
  if (Math.max(...ys) - Math.min(...ys) <= tol) return true; // degenerate sliver — nothing to rake
  const outline = outlineAlongWall(points);
  if (!outline) return true; // no length in plan — nothing to run along
  const { heights, slopeRise } = readTopEdge(outline);
  if (slopeRise > tol) return false;
  if (heights.length === 0) return true;
  return Math.max(...heights) - Math.min(...heights) <= tol;
};

module.exports = {
  WALL_TOP_BUCKET,
  MULTIMODAL_RATIO,
  modalWallTopY,
  wallTopY,
  modalCeilingY,
  wallHasFlatTop,
};
